import React, { FC, memo, useCallback } from 'react';
import { GeolocationCoords } from 'app/types/common';
import { WarehouseForBooking, getWorkingScheduleString } from 'entities/Warehouse';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ReactComponent as LocationIcon } from 'shared/assets/icons/newIcons/LocationIcon.svg';
import { ReactComponent as TimeIcon } from 'shared/assets/icons/newIcons/TimeIcon.svg';
import WarehouseDefaultImage from 'shared/assets/images/WarehouseDefaultImage.png';
import { Button } from 'shared/ui/Button';
import { ModernSizeGroupsTags } from 'entities/SizeCode';
import { getBoxDiscountedPriceAmount, getBoxPriceAmountWithVat, useGetMinBoxPriceAmount } from 'entities/Box';
import { useGetCurrencySymbol, getGlobalSettings } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getLoggedUserData } from 'entities/User';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface ModernWarehouseCardProps {
  warehouse: WarehouseForBooking;
  distance?: Nullable<number>;
  isOutlined: boolean;
  onSelectCard: (coords: GeolocationCoords) => void;
  onGetRoute: (coords: GeolocationCoords, warehouseId: string) => void;
  onClearRoute: () => void;
  onViewBoxes: () => void;
}

export const ModernWarehouseCard: FC<ModernWarehouseCardProps> = memo((props) => {
  const {
    warehouse: {
      name,
      imageUrl,
      address,
      zip,
      workingSchedule,
      sizeCodes,
      availableBoxesAmount,
      warehouseDiscounts,
      minPrice,
      longitude,
      latitude,
    },
    isOutlined,
    distance,
    onViewBoxes,
    onSelectCard,
  } = props;

  const currencySymbol = useGetCurrencySymbol();
  const globalSettings = useAppSelector(getGlobalSettings);
  const loggedUser = useAppSelector(getLoggedUserData);
  const { t } = useAppTranslation(['booking', 'common']);

  const fullAddress = `${address} ${zip || ''}`;
  const warehouseImage = imageUrl || WarehouseDefaultImage;
  const workingScheduleString = getWorkingScheduleString(workingSchedule, t);

  const minBoxPrice = useGetMinBoxPriceAmount(props.warehouse);

  const discountedPrice = getBoxDiscountedPriceAmount({ warehouseDiscounts, boxPrice: minPrice });
  const discountedBoxPriceWithVat = getBoxPriceAmountWithVat(discountedPrice, props.warehouse, loggedUser?.accountType);

  const discountedBoxPrice = globalSettings?.showPriceWithVat ? discountedBoxPriceWithVat : discountedPrice;

  const selectCard = useCallback((): void => {
    onSelectCard({ lat: latitude, lng: longitude });
  }, [latitude, longitude, onSelectCard]);

  const handleViewBoxes = useCallback((): void => {
    onViewBoxes();
  }, [onViewBoxes]);

  return (
    <div
      className={`p-4 rounded-lg overflow-hidden cursor-pointer outline outline-2 hover:outline-accent hover:outline-2 ${
        isOutlined ? 'outline-accent bg-green-50' : 'outline-gray-200 bg-white'
      }`}
      onClick={selectCard}
    >
      <div className="flex">
        <div className="w-[190px] h-[190px] flex-shrink-0 rounded-lg overflow-hidden">
          <img className="w-full h-full object-cover" src={warehouseImage} alt={`${name} warehouse`} />
        </div>

        <div className="flex-1 pl-4 flex flex-col">
          <div className="space-y-1 flex-1">
            <h3 className="text-2xl font-semibold font-tektur text-gray-800">{name}</h3>

            <div className="flex flex-col gap-1 pb-3">
              <div className="flex items-center text-grey">
                <div className="flex items-center">
                  <LocationIcon className="w-[17px] h-[17px] mr-2 text-grey" />
                  <span>{fullAddress}</span>
                </div>
                {distance && (
                  <>
                    <div className="mx-3">•</div>
                    <div>{t('{{distance}} km', { distance })}</div>
                  </>
                )}
              </div>

              <div className="flex items-center text-grey text-sm">
                <TimeIcon className="w-[18px] h-[18px] mr-2 text-grey" />
                <span>{workingScheduleString}</span>
              </div>
            </div>

            <ModernSizeGroupsTags sizeCodes={sizeCodes} />

            <div className="text-grey pt-2 mb-4">{t('{{availableBoxesAmount}} boxes free', { availableBoxesAmount })}</div>

            <div className="flex justify-between items-center">
              {Boolean(minBoxPrice) && (
                <div className="shrink-0 text-xl font-semibold font-tektur">
                  {t('From {{discountedPrice}} {{currencySymbol}}/month', {
                    currencySymbol,
                    discountedPrice: formatPrice(discountedBoxPrice || minBoxPrice),
                  })}
                </div>
              )}
              <Button onClick={handleViewBoxes}>{t('Select')}</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
